import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import TableContainer from "../Tables/TableContainer";
import TableCell from "../../pages/BillingAndPayments/components/TableCell";
import CellTypography from "../Tables/CellTypography";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { usePagination } from "../../hooks/usePagination";
import useNearScreen from "../../hooks/useNearScreen";
import {
  startGetMetaReusableCreatives,
  startSyncMetaCreativeEngagements,
} from "../../actions/adsCampaigns";
import {
  ADS_META_DESTINATION_TYPE_WEBSITE,
  BUTTON_GO_BACK,
  BUTTON_SELECT,
  CAROUSEL,
  CREATIVE,
  CREATIVE_TYPE_PLACEHOLDER,
  DESTINATION_TYPE_AD_PLACEHOLDER,
  IMAGE,
  MODAL_META_REUSE_CREATIVES_CLEAR_FILTER,
  MODAL_META_REUSE_CREATIVES_COLUMN_COMPATIBILITY,
  MODAL_META_REUSE_CREATIVES_COLUMN_ENGAGEMENT,
  MODAL_META_REUSE_CREATIVES_COLUMN_TEXTS,
  MODAL_META_REUSE_CREATIVES_DESCRIPTION,
  MODAL_META_REUSE_CREATIVES_EMPTY,
  MODAL_META_REUSE_CREATIVES_FILTER_ACTIVE,
  MODAL_META_REUSE_CREATIVES_INVALID,
  MODAL_META_REUSE_CREATIVES_OPTIMIZATION_WARN,
  MODAL_META_REUSE_CREATIVES_TITLE,
  MODAL_META_REUSE_CREATIVES_VALID,
  LIKES,
  COMMENTS,
  SHARES,
  VIDEO,
} from "../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WEBSITE,
  VIDEO_FORMATS,
} from "../../utils/constants";
import {
  buildMetaCreativeHashUrl,
  getMetaCreativeReuseCompatibility,
  isMetaCreativeEngagementCacheFresh,
} from "../../utils/metaCreativeReuse";
import { getExtensionFile } from "../../utils/string";
import _ from "lodash";
import { META_DESTINATION_TYPE_TRANSLATION_KEYS } from "../../utils/metaDestinationTypeLabels";

const PREVIEW_THUMB_SX = {
  width: 48,
  height: 48,
  objectFit: "cover",
  borderRadius: 1,
  bgcolor: "action.hover",
  flexShrink: 0,
  display: "block",
};

const isVideoPreviewUrl = (url = "", contentType = "") =>
  contentType === "video" || VIDEO_FORMATS.includes(getExtensionFile(url));

const getVideoPreviewSrc = (url = "") => {
  if (!url || url.includes("#t=")) return url;
  return `${url}#t=0.001`;
};

const CreativePreviewThumb = ({ url = "", contentType = "" }) => {
  if (!url) {
    return <Box sx={PREVIEW_THUMB_SX} />;
  }

  if (isVideoPreviewUrl(url, contentType)) {
    return (
      <Box
        component="video"
        src={getVideoPreviewSrc(url)}
        muted
        playsInline
        preload="metadata"
        controls={false}
        sx={PREVIEW_THUMB_SX}
      />
    );
  }

  return <Box component="img" src={url} alt="" sx={PREVIEW_THUMB_SX} />;
};

const CONTENT_TYPE_TRANSLATION_KEYS = {
  image: IMAGE,
  video: VIDEO,
  carousel: CAROUSEL,
};

const getContentTypeLabelKey = (contentType = "image") =>
  CONTENT_TYPE_TRANSLATION_KEYS[contentType] || IMAGE;

const getDestinationTypeLabelKey = (destinationTypeValue = "") =>
  META_DESTINATION_TYPE_TRANSLATION_KEYS[destinationTypeValue] ||
  ADS_META_DESTINATION_TYPE_WEBSITE;

/**
 * Adgroup destinationType is the source of truth for reuse display.
 * destinationTypeAd is often defaulted to WEBSITE on library entries and
 * must not override the real adgroup destination (IG messages, lead form, etc.).
 */
const getLibraryDestinationTypeForDisplay = (item = {}) =>
  item.destinationType || item.destinationTypeAd || "";

const getTitlePreview = (titles = []) => {
  const text = String(titles?.[0] || "").trim();
  if (!text) return "—";
  return text.length > 50 ? `${text.slice(0, 50)}…` : text;
};

const formatEngagementCount = (value) => {
  const count = Number(value) || 0;
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return String(count);
};

const EngagementMetric = ({ icon: Icon, label, value }) => (
  <Tooltip title={label}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Icon sx={{ fontSize: 16, color: "text.secondary" }} />
      <Typography variant="caption" color="text.secondary">
        {formatEngagementCount(value)}
      </Typography>
    </Box>
  </Tooltip>
);

const CreativeEngagementCell = ({ engagement, isSyncing, t }) => {
  if (engagement?.syncedTime) {
    const facebook = engagement.facebook || {};
    const instagram = engagement.instagram || {};
    const likes =
      (Number(facebook.reactions) || 0) + (Number(instagram.likes) || 0);
    const comments =
      (Number(facebook.comments) || 0) + (Number(instagram.comments) || 0);
    const shares = Number(facebook.shares) || 0;

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <EngagementMetric
          icon={ThumbUpOutlinedIcon}
          label={t(LIKES)}
          value={likes}
        />
        <EngagementMetric
          icon={ChatBubbleOutlineIcon}
          label={t(COMMENTS)}
          value={comments}
        />
        <EngagementMetric
          icon={ShareOutlinedIcon}
          label={t(SHARES)}
          value={shares}
        />
      </Box>
    );
  }

  if (isSyncing) {
    return <CircularProgress size={18} thickness={5} sx={{ color: "black" }} />;
  }

  return <CellTypography>—</CellTypography>;
};

const getDestinationDetail = (item = {}) => {
  const destinationTypeValue = getLibraryDestinationTypeForDisplay(item);

  if (
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WEBSITE.includes(destinationTypeValue)
  ) {
    return item.destinationUrls?.[0] || "";
  }

  if (
    destinationTypeValue ===
    ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION
  ) {
    return item.instantForm?.name || "";
  }

  return "";
};

const CompatibilityStatusIcon = ({ isValid, label }) => {
  const Icon = isValid ? CheckCircleIcon : CancelIcon;
  return (
    <Tooltip title={label}>
      <Icon
        aria-label={label}
        sx={{
          color: isValid ? "success.main" : "error.main",
          fontSize: 22,
        }}
      />
    </Tooltip>
  );
};

const ModalReuseCreativesList = ({
  modalOpen,
  advertiserID,
  adAccountID,
  pageID,
  integrationID,
  mediaUrls = [],
  isCarousel = false,
  objective,
  destinationType,
  destinationTypeAd,
  optimizationGoal,
  onCloseModal = () => {},
  onCallbackSelect = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterByHash, setFilterByHash] = useState(true);
  const [syncingCreativeIDs, setSyncingCreativeIDs] = useState({});
  const syncingCreativeIDsRef = useRef({});

  const mediaHash = useMemo(
    () => buildMetaCreativeHashUrl(mediaUrls, isCarousel),
    [mediaUrls, isCarousel]
  );

  const onLoadPage = async () => {
    if (loadLastItem) return;

    const response = await dispatch(
      startGetMetaReusableCreatives({
        advertiserID,
        adAccountID,
        pageID,
        lastKey,
        onChangeLastKey,
        onChangeLoadLastItem,
        hashUrl: filterByHash ? mediaHash || null : null,
        limit: 15,
      })
    );

    if (response.ok) {
      setItems((prev) => _.uniqBy([...prev, ...response.data], "creativeID"));
    }

    if (isLoading) setIsLoading(false);

    if (!response.ok || !integrationID) {
      return response.ok;
    }

    const staleCreativeIDs = (response.data || [])
      .filter((item) => {
        if (!item?.creativeID) return false;
        if (syncingCreativeIDsRef.current[item.creativeID]) return false;
        if (isMetaCreativeEngagementCacheFresh(item?.engagement?.syncedTime)) {
          return false;
        }
        return true;
      })
      .map((item) => item.creativeID);

    if (!staleCreativeIDs.length) {
      return response.ok;
    }

    syncingCreativeIDsRef.current = {
      ...syncingCreativeIDsRef.current,
      ...Object.fromEntries(staleCreativeIDs.map((id) => [id, true])),
    };
    setSyncingCreativeIDs({ ...syncingCreativeIDsRef.current });

    dispatch(
      startSyncMetaCreativeEngagements({
        advertiserID,
        adAccountID,
        pageID,
        integrationID,
        creativeIDs: staleCreativeIDs,
      })
    )
      .then((engagementResponse) => {
        if (!engagementResponse.ok) return;
        const engagementsByCreativeID = engagementResponse.data || {};
        setItems((prev) =>
          prev.map((item) => {
            const engagement = engagementsByCreativeID[item.creativeID];
            return engagement ? { ...item, engagement } : item;
          })
        );
      })
      .finally(() => {
        const next = { ...syncingCreativeIDsRef.current };
        staleCreativeIDs.forEach((id) => {
          delete next[id];
        });
        syncingCreativeIDsRef.current = next;
        setSyncingCreativeIDs(next);
      });

    return response.ok;
  };

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: 15,
    onCallBackNextPage: onLoadPage,
  });

  const { fromRef, loadLastItem, onChangeLoadLastItem, isNearScreen } =
    useNearScreen({
      distance: "100px",
    });

  useEffect(() => {
    setItems([]);
    setSelectedItem(null);
    setIsLoading(true);
    setSyncingCreativeIDs({});
    syncingCreativeIDsRef.current = {};
    onChangeLoadLastItem(false);
    onChangeLastKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterByHash, mediaHash, advertiserID, adAccountID, pageID]);

  useEffect(() => {
    onLoadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNearScreen, filterByHash, mediaHash]);

  const enrichedItems = useMemo(
    () =>
      items.map((item) => {
        const compat = getMetaCreativeReuseCompatibility({
          libraryEntry: item,
          objective,
          destinationType,
          destinationTypeAd,
          optimizationGoal,
        });
        return { ...item, ...compat };
      }),
    [items, objective, destinationType, destinationTypeAd, optimizationGoal]
  );

  const selectedEntry = enrichedItems.find(
    (item) => item.creativeID === selectedItem
  );

  const onConfirm = () => {
    if (!selectedEntry?.isValid) return;
    onCallbackSelect(selectedEntry);
    onCloseModal();
  };

  const onSelectRow = (item) => {
    if (!item.isValid) return;
    setSelectedItem(item.creativeID);
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="lg"
      title={t(MODAL_META_REUSE_CREATIVES_TITLE)}
      description={t(MODAL_META_REUSE_CREATIVES_DESCRIPTION)}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {Boolean(mediaHash) && (
          <FormControlLabel
            control={
              <Switch
                checked={filterByHash}
                onChange={(e) => setFilterByHash(e.target.checked)}
              />
            }
            label={t(
              filterByHash
                ? MODAL_META_REUSE_CREATIVES_FILTER_ACTIVE
                : MODAL_META_REUSE_CREATIVES_CLEAR_FILTER
            )}
          />
        )}
        <TableContainer
          loading={isLoading}
          keys={enrichedItems}
          hasPagination={false}
          emptyText={MODAL_META_REUSE_CREATIVES_EMPTY}
          ariaLabel={t(MODAL_META_REUSE_CREATIVES_TITLE)}
          sxContainer={{ boxShadow: "none" }}
          sx={{ maxHeight: "calc(100vh - 200px)" }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                isHeader={true}
                sx={{ pl: "16px !important", minWidth: 48, width: 48 }}
              />
              <TableCell isHeader={true}>{t(CREATIVE)}</TableCell>
              <TableCell isHeader={true}>
                {t(CREATIVE_TYPE_PLACEHOLDER)}
              </TableCell>
              <TableCell isHeader={true}>
                {t(DESTINATION_TYPE_AD_PLACEHOLDER)}
              </TableCell>
              <TableCell isHeader={true}>
                {t(MODAL_META_REUSE_CREATIVES_COLUMN_TEXTS)}
              </TableCell>
              <TableCell isHeader={true}>
                {t(MODAL_META_REUSE_CREATIVES_COLUMN_ENGAGEMENT)}
              </TableCell>
              <TableCell isHeader={true}>
                {t(MODAL_META_REUSE_CREATIVES_COLUMN_COMPATIBILITY)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enrichedItems.map((item, index) => {
              const previewUrl = item.mediaUrls?.[0] || "";
              const isLast = index === enrichedItems.length - 1;
              const destinationTypeValue =
                getLibraryDestinationTypeForDisplay(item);
              const destinationDetail = getDestinationDetail(item);
              const isSelected = selectedItem === item.creativeID;
              const compatibilityLabel = t(
                item.isValid
                  ? MODAL_META_REUSE_CREATIVES_VALID
                  : MODAL_META_REUSE_CREATIVES_INVALID
              );

              return (
                <TableRow
                  key={item.creativeID}
                  ref={isLast ? fromRef : undefined}
                  hover={item.isValid}
                  selected={isSelected}
                  onClick={() => onSelectRow(item)}
                  sx={{
                    cursor: item.isValid ? "pointer" : "default",
                    opacity: item.isValid ? 1 : 0.55,
                  }}
                >
                  <TableCell
                    indexRow={index}
                    isLastRow={isLast}
                    sx={{ pl: "16px !important", minWidth: 48, width: 48 }}
                  >
                    <Radio
                      checked={isSelected}
                      disabled={!item.isValid}
                      value={item.creativeID}
                      onChange={() => onSelectRow(item)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    indexRow={index}
                    isLastRow={isLast}
                    sx={{ minWidth: 200 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                      }}
                    >
                      <CreativePreviewThumb
                        url={previewUrl}
                        contentType={item.contentType}
                      />
                      <CellTypography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.creativeID}
                      </CellTypography>
                    </Box>
                  </TableCell>
                  <TableCell indexRow={index} isLastRow={isLast}>
                    <CellTypography>
                      {t(getContentTypeLabelKey(item.contentType || "image"))}
                    </CellTypography>
                  </TableCell>
                  <TableCell
                    indexRow={index}
                    isLastRow={isLast}
                    sx={{
                      minWidth: 160,
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "unset",
                    }}
                  >
                    <CellTypography>
                      {t(getDestinationTypeLabelKey(destinationTypeValue))}
                    </CellTypography>
                    {Boolean(destinationDetail) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        title={destinationDetail}
                        sx={{ display: "block", maxWidth: 200 }}
                      >
                        {destinationDetail}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    indexRow={index}
                    isLastRow={isLast}
                    sx={{
                      minWidth: 160,
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "unset",
                    }}
                  >
                    <CellTypography
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        whiteSpace: "normal",
                      }}
                    >
                      {getTitlePreview(item.titles)}
                    </CellTypography>
                  </TableCell>
                  <TableCell
                    indexRow={index}
                    isLastRow={isLast}
                    sx={{ minWidth: 140 }}
                  >
                    <CreativeEngagementCell
                      engagement={item.engagement}
                      isSyncing={Boolean(syncingCreativeIDs[item.creativeID])}
                      t={t}
                    />
                  </TableCell>
                  <TableCell indexRow={index} isLastRow={isLast}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.75,
                        alignItems: "center",
                      }}
                    >
                      <CompatibilityStatusIcon
                        isValid={item.isValid}
                        label={compatibilityLabel}
                      />
                      {item.optimizationGoalMismatch && (
                        <Chip
                          size="small"
                          color="warning"
                          label={t(
                            MODAL_META_REUSE_CREATIVES_OPTIMIZATION_WARN
                          )}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </TableContainer>
      </Box>
      <DialogActions sx={{ p: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_GO_BACK,
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: !selectedEntry?.isValid,
            text: BUTTON_SELECT,
            type: "button",
            onClick: onConfirm,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalReuseCreativesList;
